import React from 'react'
import {
  VStack,
  Card,
  CardBody,
  Box,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
} from '@chakra-ui/react'
import { UseFormRegister, FieldErrors } from 'react-hook-form'
import { AddResearchFormData } from '../hooks/useAddResearch'

interface AddResearchFormProps {
  children: React.ReactNode
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
  register: UseFormRegister<AddResearchFormData>
  errors: FieldErrors<AddResearchFormData>
}

export const AddResearchForm: React.FC<AddResearchFormProps> = ({
  children,
  onSubmit,
  register,
  errors,
}) => {
  return (
    <Box maxW='4xl' mx='auto' p={6}>
      <Card>
        <CardBody>
          <VStack spacing={8} align='stretch'>
            <Heading size='lg' color='blue.600'>
              Add Research Article
            </Heading>

            <form onSubmit={onSubmit}>
              <VStack spacing={6} align='stretch'>
                {/* Basic Information Section */}
                <VStack spacing={4} align='stretch'>
                  <FormControl isInvalid={!!errors.title}>
                    <FormLabel>Title *</FormLabel>
                    <Input
                      {...register('title', {
                        required: 'Title is required',
                        minLength: {
                          value: 10,
                          message: 'Title must be at least 10 characters',
                        },
                      })}
                      placeholder='Enter the research title'
                    />
                    <FormErrorMessage>
                      {errors.title && errors.title.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.publicationYear}>
                    <FormLabel>Publication Year *</FormLabel>
                    <Input
                      {...register('publicationYear', {
                        required: 'Publication year is required',
                        pattern: {
                          value: /^\d{4}$/,
                          message: 'Please enter a valid 4-digit year',
                        },
                      })}
                      placeholder='e.g., 2023'
                      maxLength={4}
                    />
                    <FormErrorMessage>
                      {errors.publicationYear && errors.publicationYear.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.journal}>
                    <FormLabel>Journal *</FormLabel>
                    <Input
                      {...register('journal', {
                        required: 'Journal is required',
                        minLength: {
                          value: 3,
                          message: 'Journal name must be at least 3 characters',
                        },
                      })}
                      placeholder='Enter the journal name'
                    />
                    <FormErrorMessage>
                      {errors.journal && errors.journal.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>

                {/* Publication Section */}
                <VStack spacing={4} align='stretch'>
                  <FormControl isInvalid={!!errors.url}>
                    <FormLabel>URL *</FormLabel>
                    <Input
                      type='url'
                      {...register('url', {
                        required: 'URL is required',
                        pattern: {
                          value: /^https?:\/\/.+/,
                          message:
                            'Please enter a valid URL starting with http:// or https://',
                        },
                      })}
                      placeholder='https://example.com/article'
                    />
                    <FormErrorMessage>
                      {errors.url && errors.url.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.doi}>
                    <FormLabel>DOI (Optional)</FormLabel>
                    <Input
                      {...register('doi')}
                      placeholder='e.g., 10.1234/example.doi'
                    />
                    <FormErrorMessage>
                      {errors.doi && errors.doi.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>

                {/* Content Section */}
                <VStack spacing={4} align='stretch'>
                  <FormControl isInvalid={!!errors.abstract}>
                    <FormLabel>Abstract *</FormLabel>
                    <Textarea
                      {...register('abstract', {
                        required: 'Abstract is required',
                        minLength: {
                          value: 50,
                          message: 'Abstract must be at least 50 characters',
                        },
                      })}
                      placeholder='Enter the research abstract'
                      rows={6}
                      resize='vertical'
                    />
                    <FormErrorMessage>
                      {errors.abstract && errors.abstract.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.researcherNotes}>
                    <FormLabel>Researcher Notes (Optional)</FormLabel>
                    <Textarea
                      {...register('researcherNotes')}
                      placeholder='Add any additional notes or insights about this research'
                      rows={4}
                      resize='vertical'
                    />
                    <FormErrorMessage>
                      {errors.researcherNotes && errors.researcherNotes.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>

                {children}
              </VStack>
            </form>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

export default AddResearchForm
